'use strict';

const { getDependencyClass } = require('../../lib/dependencies.js');
const performanceMeasure = require('../../lib/performanceMeasure.js');
let measure = performanceMeasure('webcomponents:stickyNav');

getDependencyClass('phoenix-page').then((pageClass) => {
    const componentTag = 'phoenix-sticky-nav';
    const bodyActiveClassName = 'is-sticky-nav-visible';
    const elementActiveClassName = 'is-visible';
    const minimumScrollBuffer = 200;
    const BALLOON_ACTIVATED = 'phoenix:balloonHeader:activated';
    const BALLOON_POPPED = 'phoenix:balloonHeader:popped';

    /**
     * Phoenix Sticky Nav is a Web Component that sets the header nav to be 'stuck' to the top
     * of the page while the user is scrolling. The nav is hidden while the user is scrolling
     * down the page, and then slides out when the user begins scrolling back up. Once the nav
     * header at the top of the page is back in view the sticky nav disappears.
     *
     * @example
     *     <phoenix-sticky-nav persistent>
     *          <span>Header Image</span>
     *          <span>Menu of some kind</span>
     *     </phoenix-sticky-nav>
     */
    class PhoenixStickyNav extends pageClass.PhoenixElement {
        get observedBroadcastedEvents() {
            return [BALLOON_ACTIVATED, BALLOON_POPPED];
        }

        connectedCallback() {
            super.connectedCallback();
            // Check if the sticky nav is persistent, meaning that it will be displayed as soon
            // as the header is out of view as opposed to only showing when the user begins
            // scrolling back up the page.
            this.persistent = this.hasAttribute('persistent');

            // Flag for suspending stickyNav while other things are going on on
            // the page. At the momeny this is controlled by a single event
            // sent by balloonHeader and can be simply a boolean; in the
            // future, if more components affect stickyNav, we'll need to track
            // state in a more fine-grained manner.
            this.suspended = false;

            // This optional attribute allows us to push the target, which must be
            // off-screen before the sticky navbar reveals itself, further down the
            // page than the fixed navbar.
            let navTarget = this.getAttribute('nav-target') || '.m-header--navbar';
            this.navTargetElement = document.querySelector(navTarget);

            if (!this.navTargetElement) {
                // eslint-disable-next-line no-console
                console.warn('No target element was found for the sticky nav.');
                return;
            }

            this._updateScrollPosition();
            let targetRect = this.navTargetElement.getBoundingClientRect();
            this.navTopOffset = targetRect.top + targetRect.height + this.scrollPosition;
            this.previousScrollPosition = this.scrollPosition;
            this._setupHeaderAdAdjustment();
            this._setupListener();
        }

        broadcastedEventCallback(name, args = {}) {
            if (name === BALLOON_ACTIVATED) {
                this.suspended = true;
                this._hide();
            } else if (name === BALLOON_POPPED) {
                this.suspended = false;
                // We want to check in case the header pops while the scroll
                // position is outside where we want stickyNav to show.
                this._checkVisibility();
            }
        }

        disconnectedCallback() {
            this._unlisten();
            super.disconnectedCallback();
        }

        _show() {
            this.isVisible = true;
            this.classList.add(elementActiveClassName);
            document.body.classList.add(bodyActiveClassName);
            this.style.visibility = 'visible';
            this.setAttribute('tabindex', '0');
        }

        _hide() {
            this.isVisible = false;
            this.classList.remove(elementActiveClassName);
            document.body.classList.remove(bodyActiveClassName);
            this.style.visibility = 'hidden';
            this.setAttribute('tabindex', '-1');
        }

        _updateScrollPosition() {
            this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
        }

        _checkShowPersistent() {
            if (this.suspended) {
                return false;
            }

            this._updateScrollPosition();
            return this.scrollPosition > this.navTopOffset;
        }

        _checkShowNonPersistent() {
            if (this.suspended) {
                return false;
            }

            this._updateScrollPosition();

            let shouldShow = false;
            if (this.scrollPosition > this.previousScrollPosition) {
                // Scrolling down the page and the nav is visible so there is a chance that it needs to be
                // hidden
                if (
                    !this.isVisible ||
                    this.scrollPosition - this.previousScrollPosition > minimumScrollBuffer
                ) {
                    shouldShow = false;
                } else {
                    shouldShow = true;
                }
            } else if (this.scrollPosition < this.previousScrollPosition) {
                // Scrolling up the page and the nav is not visible
                if (
                    this.scrollPosition > this.navTopOffset &&
                    (this.isVisible ||
                        this.previousScrollPosition - this.scrollPosition > minimumScrollBuffer)
                ) {
                    // Only show the nav when the user has scrolled more than `minimumScrollBuffer`
                    // distance and the scroll position is below the nav. The reasoning here is that
                    // it prevents the nav bar from appearing and disappearing from small scroll
                    // movements
                    shouldShow = true;
                }
            }

            this.previousScrollPosition = this.scrollPosition;
            return shouldShow;
        }

        _checkVisibility() {
            let shouldShow = this.persistent ? this._checkShowPersistent() : this._checkShowNonPersistent();
            if (!this.isVisible && shouldShow) {
                this._show();
            } else if (this.isVisible && !shouldShow) {
                this._hide();
            }
            return shouldShow;
        }

        _setupHeaderAdAdjustment() {
            // We're concerned about changes to the position of the target
            // element caused by a header ad loading, so we want to update
            // the offset if that's the case. If the advertising bootscript
            // queue is unavailable, we'll end up with a no-op.
            let self = this;
            let updateHeaderOffset = function () {
                let newTargetRect = self.navTargetElement.getBoundingClientRect();
                self._updateScrollPosition();
                self.navTopOffset = newTargetRect.top + newTargetRect.height + self.scrollPosition;
            };
            window.phxAdvertisingQueue = window.phxAdvertisingQueue || [];
            window.phxAdvertisingQueue.push(function () {
                let headerSlots = [...document.querySelectorAll('.m-header-ad [id^="ad-"]')].map(
                    (el) => el.id
                );
                headerSlots.forEach((slotId) => {
                    this.getSlotResult(slotId).then(function (result) {
                        if (result.filled) {
                            updateHeaderOffset();
                            // now, watch for mutations on this element
                            let adElement = document.getElementById(slotId);
                            self.offsetObserver = new MutationObserver(updateHeaderOffset);
                            self.offsetObserver.observe(adElement, {
                                attributes: true,
                                childList: true,
                                subtree: true,
                            });
                        }
                    });
                });
            });
        }

        _setupListener() {
            // Check right away if the nav is persistent since we may have arrived in the
            // middle of the page.
            if (this.persistent && this._checkShowPersistent()) {
                this._show();
            }

            // Setup the scroll listener, but debounce it so that it's not running on every
            // instance of the event
            this.scrollListener = pageClass.util.debounce(this._checkVisibility.bind(this), 50);
            window.addEventListener('scroll', this.scrollListener);
        }

        _unlisten() {
            if (this.scrollListener) {
                window.removeEventListener('scroll', this.scrollListener);
            }
            if (this.offsetObserver) {
                this.offsetObserver.disconnect();
            }
        }
    }

    window.customElements.define(componentTag, PhoenixStickyNav);
    measure.end();
});
